import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MarketOverviewItem } from '../../api/metrics';
import './ProductPicker.css';

interface ProductPickerProps {
  currentProductId: string;
  products: MarketOverviewItem[];
  onProductChange?: (productId: string) => void;
}

const ProductPicker: React.FC<ProductPickerProps> = ({
  currentProductId,
  products,
  onProductChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.productId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort products: current first, then alphabetically
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (a.productId === currentProductId) return -1;
    if (b.productId === currentProductId) return 1;
    return a.productId.localeCompare(b.productId);
  });

  const handleProductSelect = (productId: string) => {
    setIsOpen(false);
    setSearchTerm('');

    if (onProductChange) {
      onProductChange(productId);
    } else {
      // Navigate to the chart view for the selected product
      navigate(`/chart/${productId}`);
    }
  };

  const formatProductDisplay = (productId: string) => {
    return productId.replace('-', ' / ');
  };

  const formatPrice = (price: number) => {
    if (price >= 1) {
      return price.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } else if (price >= 0.01) {
      return price.toLocaleString('en-US', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
      });
    } else {
      return price.toLocaleString('en-US', {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6
      });
    }
  };

  const formatChangePercent = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  return (
    <div className="product-picker" ref={dropdownRef}>
      <button
        className="product-picker-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select product"
      >
        <span className="product-picker-label">Asset:</span>
        <span className="product-picker-current">{formatProductDisplay(currentProductId)}</span>
        <svg
          className={`product-picker-arrow ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="product-picker-dropdown">
          <div className="product-picker-search">
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          <div className="product-picker-list">
            {sortedProducts.length === 0 ? (
              <div className="product-picker-empty">No assets found</div>
            ) : (
              sortedProducts.map((product) => {
                const isSelected = product.productId === currentProductId;
                const changeClass = product.priceChangePercentage24h >= 0 ? 'positive' : 'negative';

                return (
                  <button
                    key={product.productId}
                    className={`product-picker-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleProductSelect(product.productId)}
                  >
                    <div className="product-picker-item-main">
                      <span className="product-picker-item-name">
                        {formatProductDisplay(product.productId)}
                      </span>
                      {isSelected && (
                        <span className="product-picker-item-badge">Current</span>
                      )}
                    </div>
                    <div className="product-picker-item-details">
                      <span className="product-picker-item-price">
                        ${formatPrice(product.currentPrice)}
                      </span>
                      <span className={`product-picker-item-change ${changeClass}`}>
                        {formatChangePercent(product.priceChangePercentage24h)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPicker;

